import type { CSSProperties } from 'react';
import { RobotStatus } from '@bhops/shared';

// design_handoff_kuun_ux/README.md Phase 1 — StatusBar (RowStatusBar).
//
// 행 좌측의 3px (또는 4px) 컬러바. 기존 <StatusPill> 컬럼을 대체하는 row-level
// 시각적 상태 표시. 디자인 시스템의 5가지 원칙 중 ③ "상태는 좌측 바" 적용.
//
// 사용 예:
//   <tr>
//     <td style={{ padding: 0 }}><StatusBar kind="robot" status="operating" /></td>
//     <td>...</td>
//   </tr>

/** robot 라이프사이클 카드 키 (UI 전용 — backend RobotStatus 와 1:1 매핑).
 *
 *  round10 요구2 — 'retirePlanned'(폐기 예정) 추가로 6→7단계.
 *
 *  2026-09-18 — 7→9단계. **카드 한 장 = 상태 하나**로 맞췄다. 종전엔 'stock'
 *  한 장이 제조 중과 재고(IN_STORAGE) 둘을 묶고 있었는데,
 *    ① 카드 이름이 '제조 중' 이라 창고에 있는 로봇이 제조 중으로 보였고,
 *    ② 숫자를 세러 갈 때 상태 드롭다운을 따라가는데 거기 '재고' 가 없어서
 *       **재고 로봇이 아무 카드에도 안 세어졌다**(띠 합 < 전체 로봇 수),
 *    ③ 카드를 눌러도 첫 상태(제조 중)로만 걸러져 재고는 못 봤다.
 *  한 장 = 한 상태로 하면 셋이 한 번에 사라진다. */
export type RobotLifecycleStage =
  | 'stock'
  | 'shipping'
  | 'installing'
  | 'operating'
  | 'other'
  | 'maintenance'
  | 'storage'
  | 'retirePlanned'
  | 'retired';

/**
 * 라이프사이클 카드의 **순서와 전수 — 정본 한 벌.** 로봇 목록의 띠와 로봇 상세의 단계
 * 표시가 둘 다 이것을 쓴다.
 *
 * ⚠ 2026-09-23 — 종전엔 두 화면이 각자 목록을 적어 두어, 9/18 에 목록 띠만 9칸으로
 *   늘리고 상세는 7칸 그대로였다. 그래서 '기타' 로봇의 상세 화면은 아무 칸도 안 켜졌고,
 *   '재고' 로봇은 종전엔 '제조 중' 이 켜지다가 아무것도 안 켜지게 됐다.
 */
export const ROBOT_LIFECYCLE_STAGES: readonly RobotLifecycleStage[] = [
  'stock',
  'shipping',
  'installing',
  'operating',
  'other', // 2026-09-18 — 운영중 바로 옆: 둘 다 나가 있지만 다른 것
  'maintenance',
  'storage', // 2026-09-18 — 회수 후 보관
  'retirePlanned', // round10 요구2 — 폐기 예정
  'retired',
];

/** 디자인의 report 6단계 (CustomerReport status 와 매핑). */
export type ReportStage = 'new' | 'triaged' | 'assigned' | 'inProgress' | 'resolved' | 'dismissed';

const ROBOT_COLORS: Record<RobotLifecycleStage, string> = {
  stock: 'var(--robot-stock)',
  shipping: 'var(--robot-shipping)',
  installing: 'var(--robot-installing)',
  operating: 'var(--robot-operating)',
  other: 'var(--robot-other)',
  maintenance: 'var(--robot-maintenance)',
  storage: 'var(--robot-storage)',
  retirePlanned: 'var(--robot-retire-planned)',
  retired: 'var(--robot-retired)',
};

const REPORT_COLORS: Record<ReportStage, string> = {
  new: 'var(--report-new)',
  triaged: 'var(--report-triaged)',
  assigned: 'var(--report-assigned)',
  inProgress: 'var(--report-in-progress)',
  resolved: 'var(--report-resolved)',
  dismissed: 'var(--report-dismissed)',
};

/**
 * Backend RobotStatus → 라이프사이클 카드 매핑. **1:1 이다**(2026-09-18).
 *
 * ⚠ 맨 아래 `default: return 'stock'` 은 **모르는 상태를 조용히 '제조 중' 으로
 *   떨어뜨린다.** 상태를 새로 늘리고 여기를 안 고치면 아무 경고 없이 제조 중 칸에
 *   섞인다(FB2 §1 의 설치 대기, round10 의 폐기 예정이 실제로 그렇게 당했다).
 *   상태를 늘릴 때 이 switch 를 반드시 같이 늘릴 것.
 */
export function robotStatusToStage(s: RobotStatus): RobotLifecycleStage {
  switch (s) {
    case RobotStatus.IN_PRODUCTION:
      return 'stock';
    // 2026-09-18 — 재고는 제 카드를 갖는다(종전엔 '제조 중' 에 묶여 있었다).
    case RobotStatus.IN_STORAGE:
      return 'storage';
    // 2026-09-18 — 행사·인증·테스트·사내로 나가 있는 로봇.
    case RobotStatus.OTHER:
      return 'other';
    case RobotStatus.READY_TO_SHIP:
      return 'shipping';
    // FB2 §1 — INSTALL_PENDING 은 라이프사이클상 'installing' 단계.
    // (이전엔 default→stock 으로 잘못 떨어져 '설치 대기' 카드 active/toggle 이
    //  stock 과 충돌했음.)
    case RobotStatus.INSTALL_PENDING:
      return 'installing';
    case RobotStatus.DEPLOYED:
      return 'operating';
    case RobotStatus.IN_AS:
      return 'maintenance';
    // round10 요구2 — 폐기 예정/완료 구분(default→stock 함정 주의).
    case RobotStatus.RETIREMENT_PLANNED:
      return 'retirePlanned';
    case RobotStatus.RETIRED:
      return 'retired';
    default:
      return 'stock';
  }
}

interface BaseProps {
  /** 막대 높이 (기본 36px — 행 높이에 맞춤). */
  height?: number;
  /** 막대 너비 (기본 3px). 긴급 표시는 4px 권장. */
  width?: number;
  /** P1 등 긴급 강조 — `kuun-pulse` keyframe 적용. */
  urgent?: boolean;
  /** 접근성용 라벨 (스크린리더). */
  'aria-label'?: string;
}

interface RobotProps extends BaseProps {
  kind: 'robot';
  /** 디자인 stage key 또는 backend RobotStatus. */
  status: RobotLifecycleStage | RobotStatus;
}

interface ReportProps extends BaseProps {
  kind: 'report';
  status: ReportStage;
}

interface CustomColorProps extends BaseProps {
  kind?: undefined;
  /** kind 미지정 시 직접 색 hex 또는 CSS variable. */
  color: string;
}

export type StatusBarProps = RobotProps | ReportProps | CustomColorProps;

export function StatusBar(props: StatusBarProps): JSX.Element {
  const { height = 36, width = 3, urgent } = props;
  let color: string;
  if (!('kind' in props) || props.kind === undefined) {
    color = props.color;
  } else if (props.kind === 'robot') {
    const stage =
      typeof props.status === 'string' && props.status in ROBOT_COLORS
        ? (props.status as RobotLifecycleStage)
        : robotStatusToStage(props.status as RobotStatus);
    color = ROBOT_COLORS[stage];
  } else {
    color = REPORT_COLORS[props.status];
  }

  const style: CSSProperties = {
    width,
    height,
    background: color,
    flexShrink: 0,
  };
  return (
    <div
      role="presentation"
      aria-label={props['aria-label']}
      className={urgent ? 'kuun-urgent' : undefined}
      style={style}
      data-testid="status-bar"
    />
  );
}
